/**
 * The search profile — what we are looking for, and what we will pay.
 *
 * ── The budget numbers are assumptions until somebody saves them ─────────────
 *
 * `landed_cost_vs_budget` is the heaviest factor in the whole score at weight
 * 20, and it is scored against exactly two numbers from this form: the target
 * and the ceiling. Until a person has typed them, whatever is in the column is
 * a placeholder — and a placeholder driving a fifth of the score has to say so
 * out loud. So every budget field that is still NULL, or that has been edited
 * and not yet saved, wears the word **assumed**, and it drops the moment the
 * save lands.
 *
 * That is the same honesty rule the cost assumptions follow: a number nobody
 * has confirmed is usable, and it is labelled.
 *
 * ── Money is integer cents on the wire ───────────────────────────────────────
 *
 * The field takes `$16,000`, `16000` or `16,000.00` and `parseDollarsToCents`
 * turns it into an integer with integer arithmetic — no `parseFloat`, ever, in
 * a money path. `searchProfiles.ts` refuses anything that is not a safe
 * non-negative integer, so a value this form sends is a value it accepts.
 *
 * ── §2, §6.1 ─────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { get, patch } from '../../lib/api'
import { useLocale, type Locale } from '../../lib/i18n'
import { parseDollarsToCents } from '../../../shared/money'
import { STRUCTURE_TYPES, structureLabel } from '../../lib/domain'
import { assumed, money, TONES } from '../../lib/format'
import { Banner, Chip, S as PS } from '../../components/detail/ui'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

interface SearchProfile {
  id: string
  name: string
  is_active: number
  max_price_cents: number | null
  target_landed_cost_cents: number | null
  max_landed_cost_cents: number | null
  min_length_ft: number | null
  max_length_ft: number | null
  min_width_ft: number | null
  max_width_ft: number | null
  max_height_ft: number | null
  structure_types: string
  preferred_structure_types: string
  min_condition: string | null
  alert_min_score: number
  alert_min_confidence: number
  radius_km: number | null
}

interface TermRow {
  id: string
  term: string
  kind: string
  lang: string
}

interface ProfileView {
  profile: SearchProfile
  terms: TermRow[]
  zoneIds: string[]
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'What we are looking for',
  intro:
    'The heaviest factor in the score is landed cost against these two numbers. Until you set them they are placeholders, and every screen that uses them says so.',
  loading: 'Loading…',
  failed: 'The search profile could not be loaded.',
  none: 'No search profile exists yet.',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved.',
  saveFailed: 'That could not be saved.',
  badMoney: 'That is not an amount. Try 16,000 or $16,000.',

  budget: 'Budget',
  target: 'Target landed cost',
  targetWhy: 'At or under this, the cost factor scores 100.',
  ceiling: 'Ceiling landed cost',
  ceilingWhy: 'At or over this, it scores 0. Between the two it is a straight line.',
  maxPrice: 'Most we will pay for the structure alone',
  maxPriceWhy: 'Before transport, taxes, repairs and placement.',

  size: 'Size',
  minLength: 'Shortest',
  maxLength: 'Longest',
  minWidth: 'Narrowest',
  maxWidth: 'Widest',
  maxHeight: 'Tallest',

  kinds: 'Kinds of structure',
  kindsWhy: 'Preferred scores 100 on the profile factor, acceptable 70, anything unlisted 30.',
  preferred: 'Preferred',
  acceptable: 'Acceptable',

  alerts: 'Alerts',
  alertScore: 'Only alert at or above this score',
  alertConfidence: 'And at or above this confidence',
  confidenceWhy:
    'A confidence floor is what stops an opportunity nobody knows anything about scoring high on the three factors that happen to look good.',

  terms: 'Search terms',
  termsNone: 'No terms yet.',
  termsWhy: 'Both languages, because half of what is posted here is in Spanish.',
  radius: 'How far to look',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Lo que estamos buscando',
  intro:
    'El factor más pesado de la puntuación es el costo puesto contra estos dos números. Hasta que los pongas son valores provisionales, y toda pantalla que los use lo dice.',
  loading: 'Cargando…',
  failed: 'No se pudo cargar el perfil de búsqueda.',
  none: 'Todavía no existe un perfil de búsqueda.',
  save: 'Guardar',
  saving: 'Guardando…',
  saved: 'Guardado.',
  saveFailed: 'Eso no se pudo guardar.',
  badMoney: 'Eso no es una cantidad. Prueba 16,000 o $16,000.',

  budget: 'Presupuesto',
  target: 'Costo puesto que buscamos',
  targetWhy: 'En o por debajo de esto, el factor de costo saca 100.',
  ceiling: 'Costo puesto máximo',
  ceilingWhy: 'En o por encima de esto, saca 0. Entre los dos es una línea recta.',
  maxPrice: 'Lo más que pagamos por la estructura sola',
  maxPriceWhy: 'Antes de transporte, impuestos, reparaciones y colocación.',

  size: 'Tamaño',
  minLength: 'Más corto',
  maxLength: 'Más largo',
  minWidth: 'Más estrecho',
  maxWidth: 'Más ancho',
  maxHeight: 'Más alto',

  kinds: 'Tipos de estructura',
  kindsWhy: 'Preferido saca 100 en el factor de perfil, aceptable 70, y cualquier otro 30.',
  preferred: 'Preferido',
  acceptable: 'Aceptable',

  alerts: 'Alertas',
  alertScore: 'Avisar solo con esta puntuación o más',
  alertConfidence: 'Y con esta confianza o más',
  confidenceWhy:
    'El piso de confianza es lo que evita que una oportunidad de la que no se sabe nada saque puntuación alta con los tres factores que por casualidad se ven bien.',

  terms: 'Términos de búsqueda',
  termsNone: 'Todavía no hay términos.',
  termsWhy: 'En los dos idiomas, porque la mitad de lo que se publica aquí está en español.',
  radius: 'Hasta dónde buscar',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const F = {
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, marginTop: 16 } as const,
  input: {
    width: '100%',
    minHeight: 52,
    fontSize: 17,
    padding: '12px 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  } as const,
  heading: { fontSize: 15, fontWeight: 800, margin: '22px 0 0' } as const,
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 } as const,
  chip: (on: boolean) => ({
    minHeight: 44,
    padding: '0 14px',
    borderRadius: 999,
    fontSize: 15,
    border: `1px solid ${on ? 'var(--brand-strong)' : 'var(--line)'}`,
    background: on ? 'var(--brand-soft)' : 'var(--surface)',
    color: 'var(--ink)',
    cursor: 'pointer',
  }),
  primary: (busy: boolean) => ({
    width: '100%',
    minHeight: 56,
    marginTop: 24,
    borderRadius: 'var(--radius)',
    border: 'none',
    background: busy ? 'var(--muted)' : 'var(--brand)',
    color: 'var(--brand-ink)',
    fontSize: 18,
    fontWeight: 800,
    cursor: busy ? 'progress' : 'pointer',
  }),
}

const parseList = (raw: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
  } catch {
    return []
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

interface Draft {
  targetLanded: string
  maxLanded: string
  maxPrice: string
  minLengthFt: string
  maxLengthFt: string
  minWidthFt: string
  maxWidthFt: string
  maxHeightFt: string
  alertMinScore: string
  alertMinConfidence: string
  radiusKm: string
  preferred: string[]
  acceptable: string[]
}

const numberField = (value: number | null): string => (value === null ? '' : String(value))
const centsField = (value: number | null): string => (value === null ? '' : String(value / 100))

function draftOf(profile: SearchProfile): Draft {
  return {
    targetLanded: centsField(profile.target_landed_cost_cents),
    maxLanded: centsField(profile.max_landed_cost_cents),
    maxPrice: centsField(profile.max_price_cents),
    minLengthFt: numberField(profile.min_length_ft),
    maxLengthFt: numberField(profile.max_length_ft),
    minWidthFt: numberField(profile.min_width_ft),
    maxWidthFt: numberField(profile.max_width_ft),
    maxHeightFt: numberField(profile.max_height_ft),
    alertMinScore: String(profile.alert_min_score),
    alertMinConfidence: String(profile.alert_min_confidence),
    radiusKm: numberField(profile.radius_km),
    preferred: parseList(profile.preferred_structure_types),
    acceptable: parseList(profile.structure_types),
  }
}

export default function SearchProfileForm() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const [view, setView] = useState<ProfileView | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'failed'>('loading')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const list = await get<{ profiles: (SearchProfile & { terms: TermRow[]; zoneIds: string[] })[] }>(
        '/api/search-profiles',
      )
      const chosen = list.profiles.find((row) => row.is_active === 1) ?? list.profiles[0]
      if (!chosen) {
        setState('empty')
        return
      }
      setView({ profile: chosen, terms: chosen.terms ?? [], zoneIds: chosen.zoneIds ?? [] })
      setDraft(draftOf(chosen))
      setState('ready')
    } catch {
      setState('failed')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(async () => {
    if (!view || !draft) return
    setBusy(true)
    setMessage(null)

    const body: Record<string, unknown> = {}

    // Money: integer cents or an explicit null. `parseDollarsToCents` never
    // throws and returns null for anything that is not an amount, so a typo is
    // a message rather than a NaN quietly reaching the wire.
    const cents = (raw: string): number | null | 'bad' => {
      const trimmed = raw.trim()
      if (trimmed === '') return null
      const parsed = parseDollarsToCents(trimmed)
      return parsed === null || parsed < 0 ? 'bad' : parsed
    }
    const moneyFields: [keyof Draft, string][] = [
      ['targetLanded', 'targetLandedCostCents'],
      ['maxLanded', 'maxLandedCostCents'],
      ['maxPrice', 'maxPriceCents'],
    ]
    for (const [field, key] of moneyFields) {
      const value = cents(String(draft[field]))
      if (value === 'bad') {
        setMessage({ tone: 'warn', text: t('badMoney') })
        setBusy(false)
        return
      }
      body[key] = value
    }

    const numeric: [keyof Draft, string][] = [
      ['minLengthFt', 'minLengthFt'],
      ['maxLengthFt', 'maxLengthFt'],
      ['minWidthFt', 'minWidthFt'],
      ['maxWidthFt', 'maxWidthFt'],
      ['maxHeightFt', 'maxHeightFt'],
      ['alertMinScore', 'alertMinScore'],
      ['alertMinConfidence', 'alertMinConfidence'],
      ['radiusKm', 'radiusKm'],
    ]
    for (const [field, key] of numeric) {
      const raw = String(draft[field]).trim()
      if (raw === '') {
        body[key] = null
        continue
      }
      const num = Number(raw)
      if (Number.isFinite(num)) body[key] = num
    }

    body.preferredStructureTypes = draft.preferred
    // Preferred types are implicitly acceptable. Sending a preferred type that
    // is not in the acceptable set would score it 30 on `profile_match`, which
    // is the opposite of what the person just said.
    body.structureTypes = [...new Set([...draft.acceptable, ...draft.preferred])]

    try {
      await patch<{ ok: boolean }>(`/api/search-profiles/${encodeURIComponent(view.profile.id)}`, body)
      setMessage({ tone: 'ok', text: t('saved') })
      await load()
    } catch {
      setMessage({ tone: 'warn', text: t('saveFailed') })
    } finally {
      setBusy(false)
    }
  }, [draft, view, load, t])

  if (state === 'loading') return <p style={PS.note}>{t('loading')}</p>
  if (state === 'failed') return <p style={PS.note}>{t('failed')}</p>
  if (state === 'empty') return <p style={PS.note}>{t('none')}</p>
  if (!view || !draft) return null

  const set = (patchDraft: Partial<Draft>) => setDraft({ ...draft, ...patchDraft })

  /**
   * Still an assumption?
   *
   * Either nothing was ever stored, or what is in the box no longer matches
   * what is stored. Both mean "this number has not been confirmed", and both
   * have to say so before somebody trusts a score built on it.
   */
  const isAssumed = (stored: number | null, typed: string): boolean =>
    stored === null || centsField(stored) !== typed.trim()

  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>{t('title')}</h2>
      <p style={PS.note}>{t('intro')}</p>

      {message && (
        <Banner tone={message.tone === 'ok' ? TONES.strong : TONES.warn} icon={message.tone === 'ok' ? 'check' : 'alert'}>
          {message.text}
        </Banner>
      )}

      {/* ── Budget ──────────────────────────────────────────────────────── */}
      <h3 style={F.heading}>{t('budget')}</h3>

      <MoneyField
        id="hh-sp-target"
        label={t('target')}
        hint={t('targetWhy')}
        value={draft.targetLanded}
        stored={view.profile.target_landed_cost_cents}
        assumedNow={isAssumed(view.profile.target_landed_cost_cents, draft.targetLanded)}
        locale={locale}
        onChange={(next) => set({ targetLanded: next })}
      />

      <MoneyField
        id="hh-sp-ceiling"
        label={t('ceiling')}
        hint={t('ceilingWhy')}
        value={draft.maxLanded}
        stored={view.profile.max_landed_cost_cents}
        assumedNow={isAssumed(view.profile.max_landed_cost_cents, draft.maxLanded)}
        locale={locale}
        onChange={(next) => set({ maxLanded: next })}
      />

      <MoneyField
        id="hh-sp-maxprice"
        label={t('maxPrice')}
        hint={t('maxPriceWhy')}
        value={draft.maxPrice}
        stored={view.profile.max_price_cents}
        assumedNow={isAssumed(view.profile.max_price_cents, draft.maxPrice)}
        locale={locale}
        onChange={(next) => set({ maxPrice: next })}
      />

      {/* ── Size ────────────────────────────────────────────────────────── */}
      <h3 style={F.heading}>{t('size')}</h3>
      {(
        [
          ['minLengthFt', t('minLength')],
          ['maxLengthFt', t('maxLength')],
          ['minWidthFt', t('minWidth')],
          ['maxWidthFt', t('maxWidth')],
          ['maxHeightFt', t('maxHeight')],
        ] as const
      ).map(([field, label]) => (
        <div key={field}>
          <label style={F.label} htmlFor={`hh-sp-${field}`}>
            {label}
          </label>
          <input
            id={`hh-sp-${field}`}
            style={F.input}
            value={String(draft[field])}
            inputMode="decimal"
            onChange={(event) => set({ [field]: event.target.value } as Partial<Draft>)}
          />
        </div>
      ))}

      {/* ── Kinds ───────────────────────────────────────────────────────── */}
      <h3 style={F.heading}>{t('kinds')}</h3>
      <p style={PS.note}>{t('kindsWhy')}</p>

      <span style={F.label}>{t('preferred')}</span>
      <div style={F.chipRow}>
        {STRUCTURE_TYPES.map((kind) => (
          <button
            key={kind}
            type="button"
            style={F.chip(draft.preferred.includes(kind))}
            onClick={() => set({ preferred: toggle(draft.preferred, kind) })}
          >
            {structureLabel(kind, locale)}
          </button>
        ))}
      </div>

      <span style={F.label}>{t('acceptable')}</span>
      <div style={F.chipRow}>
        {STRUCTURE_TYPES.map((kind) => (
          <button
            key={kind}
            type="button"
            style={F.chip(draft.acceptable.includes(kind) || draft.preferred.includes(kind))}
            onClick={() => set({ acceptable: toggle(draft.acceptable, kind) })}
          >
            {structureLabel(kind, locale)}
          </button>
        ))}
      </div>

      {/* ── Alerts ──────────────────────────────────────────────────────── */}
      <h3 style={F.heading}>{t('alerts')}</h3>

      <label style={F.label} htmlFor="hh-sp-score">
        {t('alertScore')}
      </label>
      <input
        id="hh-sp-score"
        style={F.input}
        value={draft.alertMinScore}
        inputMode="numeric"
        onChange={(event) => set({ alertMinScore: event.target.value })}
      />

      <label style={F.label} htmlFor="hh-sp-confidence">
        {t('alertConfidence')}
      </label>
      <input
        id="hh-sp-confidence"
        style={F.input}
        value={draft.alertMinConfidence}
        inputMode="numeric"
        onChange={(event) => set({ alertMinConfidence: event.target.value })}
      />
      <p style={PS.note}>{t('confidenceWhy')}</p>

      <label style={F.label} htmlFor="hh-sp-radius">
        {t('radius')} (km)
      </label>
      <input
        id="hh-sp-radius"
        style={F.input}
        value={draft.radiusKm}
        inputMode="decimal"
        onChange={(event) => set({ radiusKm: event.target.value })}
      />

      {/* ── Terms ───────────────────────────────────────────────────────── */}
      <h3 style={F.heading}>{t('terms')}</h3>
      <p style={PS.note}>{t('termsWhy')}</p>
      {view.terms.length === 0 ? (
        <p style={PS.note}>{t('termsNone')}</p>
      ) : (
        <div style={F.chipRow}>
          {view.terms.map((term) => (
            <Chip key={term.id} tone={term.kind === 'exclude' ? TONES.danger : TONES.quiet} outlined>
              {term.term} · {term.lang}
            </Chip>
          ))}
        </div>
      )}

      <button type="button" style={F.primary(busy)} onClick={() => void save()} disabled={busy}>
        {busy ? t('saving') : t('save')}
      </button>
    </div>
  )
}

function MoneyField({
  id,
  label,
  hint,
  value,
  stored,
  assumedNow,
  locale,
  onChange,
}: {
  id: string
  label: string
  hint: string
  value: string
  stored: number | null
  assumedNow: boolean
  locale: Locale
  onChange: (next: string) => void
}) {
  return (
    <div>
      <label style={F.label} htmlFor={id}>
        {label}
        {assumedNow && (
          <span style={{ marginLeft: 8 }}>
            <Chip tone={TONES.quiet} outlined>
              {assumed(locale)}
            </Chip>
          </span>
        )}
      </label>
      <input
        id={id}
        style={F.input}
        value={value}
        inputMode="decimal"
        placeholder="$"
        onChange={(event) => onChange(event.target.value)}
      />
      <p style={{ ...PS.note, marginTop: 6 }}>
        {hint}
        {stored !== null && ` — ${money(stored, locale)}`}
      </p>
    </div>
  )
}
